
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const initialFormState = {
  full_name: '',
  mobile: '',
  gender: '',
  date_of_birth: '',
  class_level: '',
};

const MobileSignupPage = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1: enter details, 2: enter OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendInfo, setResendInfo] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const resendIntervalRef = useRef(null);
  const today = new Date().toISOString().split('T')[0];
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setIsResending(true);
    setResendInfo('');
    setError('');
    try {
      const payload = {
        mobile: formData.mobile,
        full_name: formData.full_name,
        class_level: formData.class_level,
        gender: formData.gender,
        date_of_birth: formData.date_of_birth,
        purpose: 'signup',
      };
      const res = await requestOtp(payload);
      setIsResending(false);
      setResendInfo('OTP resent. Please check your mobile.');
      const retryAfter = (res && res.retry_after) || 30;
      setResendTimer(Number(retryAfter) || 30);
    } catch (err) {
      setIsResending(false);
      const status = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      const retryAfter = err?.response?.data?.retry_after || err?.response?.data?.retryAfter;
      if (status === 429 && retryAfter) {
        setResendTimer(Number(retryAfter) || 30);
        setError('Too many requests. Try again later.');
      } else if (status === 400) {
        if (errorCode === 'MOBILE_ALREADY_EXISTS') {
          setError('This mobile is already registered. Please sign in instead.');
        } else if (errorCode === 'MISSING_SIGNUP_DATA') {
          setError('Please complete all required signup details and try again.');
        } else {
          setError(err?.response?.data?.message || 'Invalid request.');
        }
      } else {
          if (status === 500 || errorCode === 'SMS_SEND_FAILED') {
            setError('Internal Server Error. Please try again later.');
          } else {
            setError('Internal Server Error. Please try again later.');
        }
      }
    }
  };

  useEffect(() => {
    if (resendTimer <= 0) {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
      }
      return;
    }

    if (!resendIntervalRef.current) {
      resendIntervalRef.current = setInterval(() => {
        setResendTimer((t) => {
          if (t <= 1) {
            clearInterval(resendIntervalRef.current);
            resendIntervalRef.current = null;
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }

    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
      }
    };
  }, [resendTimer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    // basic validation
    if (!/^\d{10}$/.test(formData.mobile)) {
      setError('Please enter a valid 10-digit mobile number.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        mobile: formData.mobile,
        full_name: formData.full_name,
        class_level: formData.class_level,
        gender: formData.gender,
        date_of_birth: formData.date_of_birth,
        purpose: 'signup',
      };
      const res = await requestOtp(payload);
      setStep(2);
      setLoading(false);
      const retryAfter = (res && res.retry_after) || 30;
      setResendTimer(Number(retryAfter) || 30);
      setResendInfo(`OTP sent to ${formData.mobile}`);
    } catch (err) {
      setLoading(false);
      const status = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      const retryAfter = err?.response?.data?.retry_after || err?.response?.data?.retryAfter;
      if (status === 429 && retryAfter) {
        setResendTimer(Number(retryAfter) || 30);
        setError('Too many requests. Try again later.');
      } else if (status === 400) {
        if (errorCode === 'MOBILE_ALREADY_EXISTS') {
          setError('This mobile is already registered. Please sign in instead.');
        } else if (errorCode === 'MISSING_SIGNUP_DATA') {
          setError('Please complete all required signup details and try again.');
        } else {
          setError(err?.response?.data?.message || 'Invalid signup data.');
        }
      } else {
          if (status === 500 || errorCode === 'SMS_SEND_FAILED') {
            setError('Internal Server Error. Please try again later.');
          } else {
            setError('Internal Server Error. Please try again later.');
        }
      }
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!/^[0-9]{4,6}$/.test(otp)) {
      setError('Please enter a valid code.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        mobile: formData.mobile,
        code: otp,
        purpose: 'signup',
        full_name: formData.full_name,
        class_level: formData.class_level,
        gender: formData.gender,
        date_of_birth: formData.date_of_birth,
      };
      const res = await verifyOtp(payload);
      setLoading(false);
      if (res?.access) {
        navigate('/home');
      } else {
        setError('Verification failed.');
      }
    } catch (err) {
      setLoading(false);
      const status = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      if (status === 400) {
        if (errorCode === 'INVALID_OR_EXPIRED_CODE') {
          setError('Invalid or expired code. Please re-enter or resend a new OTP.');
        } else if (errorCode === 'MOBILE_ALREADY_EXISTS') {
          setError('This mobile is already registered. Please sign in instead.');
        } else if (errorCode === 'MISSING_SIGNUP_DATA') {
          setError('Please complete all required signup details and try again.');
        } else {
          setError(err?.response?.data?.message || 'Invalid code.');
        }
      } else {
        setError('Unable to verify OTP. Please try again later.');
      }
    }
  };

  const navigate = useNavigate();
  const { requestOtp, verifyOtp } = useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-default py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white rounded shadow p-8">
        <h2 className="mb-6 text-2xl font-bold text-center text-primary">Sign Up</h2>
        {step === 1 && (
          <>
            <p className="mb-4 text-center text-sm text-text-secondary">You will use OTP sent to your mobile for signing in, no password or email required.</p>
            <form className="space-y-4" onSubmit={handleSendOtp}>
            <div>
              <label className="block text-sm font-medium text-text-secondary" htmlFor="full_name">Full Name<span className="text-error ml-1">*</span></label>
              <input
                id="full_name"
                name="full_name"
                type="text"
                required
                className="input-field w-full"
                placeholder="Full Name"
                value={formData.full_name}
                onChange={handleChange}
                maxLength={50}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary" htmlFor="class_level">Class<span className="text-error ml-1">*</span></label>
              <select
                id="class_level"
                name="class_level"
                required
                className="input-field w-full"
                value={formData.class_level}
                onChange={handleChange}
              >
                <option value="">Select class</option>
                <option value="Class 8">Class 8</option>
                <option value="Class 9">Class 9</option>
                <option value="Class 10">Class 10</option>
                <option value="Class 11">Class 11</option>
                <option value="Class 12">Class 12</option>
                <option value="Dropper">Dropper</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary" htmlFor="mobile">Mobile Number<span className="text-error ml-1">*</span></label>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                required
                className="input-field w-full"
                placeholder="10-digit mobile number"
                value={formData.mobile}
                onChange={handleChange}
                maxLength={10}
                inputMode="numeric"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary" htmlFor="gender">Gender<span className="text-error ml-1">*</span></label>
              <select
                id="gender"
                name="gender"
                required
                className="input-field w-full"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary" htmlFor="date_of_birth">Date of Birth<span className="text-error ml-1">*</span></label>
              <input
                id="date_of_birth"
                name="date_of_birth"
                type="date"
                required
                className="input-field w-full"
                value={formData.date_of_birth}
                onChange={handleChange}
                max={today}
              />
            </div>
            {error && <div className="mb-2 text-red-500">{error}</div>}
            <button type="submit" className="btn-primary w-full h-12 text-base" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Sign Up'}
            </button>
          </form>
          </>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary" htmlFor="otp">Verification Code<span className="text-error ml-1">*</span></label>
              <input
                id="otp"
                name="otp"
                type="text"
                required
                className="input-field md:max-w-sm w-full"
                value={otp}
                onChange={e => setOtp(e.target.value)}
                maxLength={6}
                inputMode="numeric"
                placeholder="Enter code"
              />
              <p className="mt-2 text-sm text-text-secondary">Didn’t get the OTP? Check your SMS inbox or try resending.</p>
            </div>
            {/* Verify button first */}
            <button
              type="button"
              className="btn-primary w-full h-12 text-base"
              onClick={handleVerifyOtp}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify & Sign Up'}
            </button>
            {/* Resend button second */}
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isResending || resendTimer > 0}
              className={`btn-secondary w-full h-12 text-base ${isResending || resendTimer > 0 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
              aria-disabled={isResending || resendTimer > 0}
            >
              {isResending ? 'Resending…' : resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : 'Resend OTP'}
            </button>
            {/* Centered message after both buttons */}
            {(resendInfo || error) && (
              <div className={`text-sm ${error ? 'text-error' : 'text-success'}`} style={{ textAlign: 'center' }}>
                {error || resendInfo}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSignupPage;
